import * as generated from "./generated/api"
import * as portableFetch from "portable-fetch";
import _merge from 'lodash-es/merge';

const baseOptions = {
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json;charset=UTF-8', // fixes response status 415
        'Accept': 'application/json' // fixes response status 406
    }
};

export abstract class BaseAPI extends generated.BaseAPI {

    public initialized: Promise<void>;

    // noinspection JSMethodCanBeStatic
    protected options(options?: any) {
        return _merge({}, baseOptions, options);
    }

    // noinspection JSMethodCanBeStatic
    /**
     * Use this method to load data from the server to initialize.
     */
    public loadData() {
        return Promise.resolve();
    }

    public initialize() {
        // noinspection JSUnusedLocalSymbols
        this.initialized = this.loadData().catch(error => {
            // Do nothing because during initialization loadData may fail because
            // of missing authorization.
        });
        return this;
    }
}


export class DestinationsApi extends BaseAPI implements generated.DestinationsApi {
    public delegate: generated.DestinationsApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.DestinationsApi(this.configuration, basePath, fetch);
    }

    getContinents(options?: any) {
        return this.delegate.getContinents(this.options(options));
    }

    getDestinationById(id: string, options?: any) {
        return this.delegate.getDestinationById(id, this.options(options));
    }

    getDestinationFilters(type: string, options?: any) {
        return this.delegate.getDestinationFilters(type, this.options(options));
    }

    getDestinations(options?: any) {
        return this.delegate.getDestinations(this.options(options));
    }
}

export class MyTripsApi extends BaseAPI implements generated.MyTripsApi {
    public delegate: generated.MyTripsApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.MyTripsApi(this.configuration, basePath, fetch);
    }

    checkIfTripExists(tripId: string, options?: any) {
        return this.delegate.checkIfTripExists(tripId, this.options(options));
    }

    createPredefinedTrip(templateId: string, dummyObject: any, options?: any) {
        return this.delegate.createPredefinedTrip(templateId, dummyObject, this.options(options));
    }

    createTrip(trip: generated.TripDto, options?: any) {
        return this.delegate.createTrip(trip, this.options(options));
    }

    createTripDestination(tripId: string, destination: any, options?: any) {
        return this.delegate.createTripDestination(tripId, destination, this.options(options));
    }

    deleteTrip(tripId: string, options?: any) {
        return this.delegate.deleteTrip(tripId, this.options(options));
    }

    deleteTripDestination(tripId: string, destinationId: string, options?: any) {
        return this.delegate.deleteTripDestination(tripId, destinationId, this.options(options));
    }

    getOrCreateAnonymousTripById(tripId: string, options?: any) {
        return this.delegate.getOrCreateAnonymousTripById(tripId, this.options(options));
    }

    getTripById(tripId: string, options?: any) {
        return this.delegate.getTripById(tripId, this.options(options));
    }

    getTripByShareId(shareId: string, options?: any) {
        return this.delegate.getTripByShareId(shareId, this.options(options));
    }

    getTrips(options?: any) {
        return this.delegate.getTrips(this.options(options));
    }

    getUndefinedTrips(options?: any) {
        return this.delegate.getUndefinedTrips(this.options(options));
    }

    updateTrip(tripId: string, trip: generated.TripDto, shareType?: string, options?: any) {
        return this.delegate.updateTrip(tripId, trip, shareType, this.options(options));
    }

    getChecklistTrips(options?: any) {
        return this.delegate.getChecklistTrips(this.options(options));
    }

    copyTrip(tripId: string, dummyObject: any, options?: any) {
        return this.delegate.copyTrip(tripId, dummyObject, this.options(options));
    }

    createUserTemplate(userTemplate: generated.UserTemplateDto, options?: any) {
        return this.delegate.createUserTemplate(userTemplate, this.options(options));
    }

    createUserdefinedTrip(tripId: string, dummyBody: any, options?: any) {
        return this.delegate.createUserdefinedTrip(tripId, dummyBody, this.options(options));
    }

    deleteUserTemplate(tripId: string, options?: any) {
        return this.delegate.deleteUserTemplate(tripId, this.options(options));
    }

    getUserTemplates(options?: any) {
        return this.delegate.getUserTemplates(this.options(options));
    }
}

export class MyTripChecklistApi extends BaseAPI implements generated.MyTripChecklistApi {
    public delegate: generated.MyTripChecklistApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.MyTripChecklistApi(this.configuration, basePath, fetch);
    }

    createTripChecklist(tripId: string, checklist: generated.ChecklistDto, options?: any) {
        return this.delegate.createTripChecklist(tripId, checklist, this.options(options));
    }

    createTripChecklistCategory(tripId: string, category: generated.ChecklistCategoryDto, options?: any) {
        return this.delegate.createTripChecklistCategory(tripId, category, this.options(options));
    }

    createTripChecklistItem(tripId: string, item: generated.ChecklistItemDto, options?: any) {
        return this.delegate.createTripChecklistItem(tripId, item, this.options(options));
    }

    deleteTripChecklist(tripId: string, options?: any) {
        return this.delegate.deleteTripChecklist(tripId, this.options(options));
    }

    deleteTripChecklistCategory(tripId: string, categoryId: number, options?: any) {
        return this.delegate.deleteTripChecklistCategory(tripId, categoryId, this.options(options));
    }

    deleteTripChecklistItem(tripId: string, itemId: number, options?: any) {
        return this.delegate.deleteTripChecklistItem(tripId, itemId, this.options(options));
    }

    getTripChecklist(tripId: string, options?: any) {
        return this.delegate.getTripChecklist(tripId, this.options(options));
    }

    getTripChecklistByShareId(shareId: string, options?: any) {
        return this.delegate.getTripChecklistByShareId(shareId, this.options(options));
    }

    getTripChecklistCategories(tripId: string, options?: any) {
        return this.delegate.getTripChecklistCategories(tripId, this.options(options));
    }

    createChecklistPdf(tripId: string, options?: any) {
        const localVarFetchArgs = generated.MyTripChecklistApiFetchParamCreator(this.configuration).createChecklistPdf(tripId, options);
        return portableFetch((<any>this.delegate).basePath + localVarFetchArgs.url, localVarFetchArgs.options).then((response) => {
            if (response.status >= 200 && response.status < 300) {
                return response.arrayBuffer();
            } else {
                throw response;
            }
        });
    }

    getTripChecklistFilters(tripId: string, options?: any) {
        return this.delegate.getTripChecklistFilters(tripId, this.options(options));
    }

    reorderTripChecklistCategories(tripId: string, reorderedCategories: any, options?: any) {
        return this.delegate.reorderTripChecklistCategories(tripId, reorderedCategories, this.options(options));
    }

    reorderTripChecklistCategoryItems(tripId: string, categoryId: number, reorderedItems: any, options?: any) {
        return this.delegate.reorderTripChecklistCategoryItems(tripId, categoryId, reorderedItems, this.options(options));
    }

    updateTripChecklist(tripId: string, checklist: generated.ChecklistDto, options?: any) {
        return this.delegate.updateTripChecklist(tripId, checklist, this.options(options));
    }

    updateTripChecklistCategory(tripId: string, categoryId: number, category: generated.ChecklistCategoryDto,
                                options?: any) {
        return this.delegate.updateTripChecklistCategory(tripId, categoryId, category, this.options(options));
    }

    updateTripChecklistItem(tripId: string, itemId: number, item: generated.ChecklistItemDto, options?: any) {
        return this.delegate.updateTripChecklistItem(tripId, itemId, item, this.options(options));
    }

    updateTripChecklistItemState(tripId: string, itemId: number, state: generated.ChecklistItemStateDto,
                                 options?: any) {
        return this.delegate.updateTripChecklistItemState(tripId, itemId, state, this.options(options));
    }

    updateTripChecklistItemTag(tripId: string, itemId: number, tag: generated.ChecklistItemTagDto, options?: any) {
        return this.delegate.updateTripChecklistItemTag(tripId, itemId, tag, this.options(options));
    }

    checkAllTripChecklistItems(tripId: string, dummyObject: any, options?: any) {
        return this.delegate.checkAllTripChecklistItems(tripId, dummyObject, this.options(options));
    }

    checkTripChecklistItems(tripId: string, checklistDto: generated.ChecklistDto, options?: any) {
        return this.delegate.checkTripChecklistItems(tripId, checklistDto, this.options(options));
    }

    moveTripCheckListItemToCategory(tripId: string, itemId: number, categoryId: number, dummyObject: any, options?: any) {
        return this.delegate.moveTripCheckListItemToCategory(tripId, itemId, categoryId, dummyObject, this.options(options));
    }

    uncheckAllTripChecklistItems(tripId: string, dummyObject: any, options?: any) {
        return this.delegate.uncheckAllTripChecklistItems(tripId, dummyObject, this.options(options));
    }

    uncheckTripChecklistItems(tripId: string, checklistDto: generated.ChecklistDto, options?: any) {
        return this.delegate.uncheckTripChecklistItems(tripId, checklistDto, this.options(options));
    }

    assignItemHandler(tripId: string, itemId: number, handler?: generated.ParticipantDto, options?: any) {
        return this.delegate.assignItemHandler(tripId, itemId, handler, this.options(options));
    }

    unassignItemHandler(tripId: string, itemId: number, options?: any) {
        return this.delegate.unassignItemHandler(tripId, itemId, this.options(options));
    }

    setTripPredefinedChecklist(tripId: string, templateId: string, dummyBody: any, options?: any) {
        return this.delegate.setTripPredefinedChecklist(tripId, templateId, dummyBody, this.options(options));
    }

    setTripUserdefinedChecklist(tripId: string, templateTripId: string, dummyBody: any, options?: any) {
        return this.delegate.setTripUserdefinedChecklist(tripId, templateTripId, dummyBody, this.options(options));
    }

    public getTripChecklistItemsCount(tripId: string, options?: any): Promise<generated.DataWrapperChecklistItemsCountDto> {
        return this.delegate.getTripChecklistItemsCount(tripId, this.options(options));
    }
}

export class MyTripPicturesApi extends BaseAPI implements generated.MyTripPicturesApi {
    public delegate: generated.MyTripPicturesApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.MyTripPicturesApi(this.configuration, basePath, fetch);
    }

    getTripPictures(options?: any) {
        return this.delegate.getTripPictures(this.options(options));
    }

    uploadPicture(uploadDto: generated.UploadDto, options?: any) {
        return this.delegate.uploadPicture(uploadDto, this.options(options));
    }
}

export class PublicApi extends BaseAPI implements generated.PublicApi {
    public delegate: generated.PublicApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.PublicApi(this.configuration, basePath, fetch);
    }

    getFilterByName(name: string, options?: any) {
        return this.delegate.getFilterByName(name, this.options(options));
    }

    getFilters(options?: any) {
        return this.delegate.getFilters(this.options(options));
    }

    getTemplates(options?: any) {
        return this.delegate.getTemplates(this.options(options));
    }
}

export class EmarsysApi extends BaseAPI implements generated.EmarsysApi {

    public delegate: generated.EmarsysApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.EmarsysApi(this.configuration, basePath, fetch);
    }

    public sendChecklist(tripId: string, email: generated.EmailDto, options?: any) {
        return this.delegate.sendChecklist(tripId, email, this.options(options));
    }

    sendSharedTripInvitation(tripId: string, invitation: generated.EmailDto, options?: any) {
        return this.delegate.sendSharedTripInvitation(tripId, invitation, this.options(options));
    }
}

export class TranslationsApi extends BaseAPI implements generated.TranslationApi {

    public delegate: generated.TranslationApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.TranslationApi(this.configuration, basePath, fetch);
    }

    getTranslation(lang: string, options?: any) {
        return this.delegate.getTranslation(lang, this.options(options));
    }
}

export class TravelAppConfigApi extends BaseAPI implements generated.TravelAppConfigApi {

    public delegate: generated.TravelAppConfigApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.TravelAppConfigApi(this.configuration, basePath, fetch);
    }

    getTravelAppConfig(lang: string, options?: any) {
        return this.delegate.getTravelAppConfig(lang, this.options(options));
    }
}

export class MyTripNotificationsApi extends BaseAPI implements generated.MyTripNotificationsApi {

    public delegate: generated.MyTripNotificationsApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.MyTripNotificationsApi(this.configuration, basePath, fetch);
    }

    getTripNotifications(tripId: string, options?: any) {
        return this.delegate.getTripNotifications(tripId, this.options(options));
    }

    createTripNotification(tripId: string, notification: generated.NotificationDto, options?: any) {
        return this.delegate.createTripNotification(tripId, notification, this.options(options));
    }

    deleteTripNotification(tripId: string, notificationId: number, options?: any) {
        return this.delegate.deleteTripNotification(tripId, notificationId, this.options(options));
    }

    updateTripNotification(tripId: string, notificationId: number, notification: generated.NotificationDto,
                           options?: any) {
        return this.delegate.updateTripNotification(tripId, notificationId, notification, this.options(options));
    }
}

export class MyTripParticipantsApi extends BaseAPI implements generated.MyTripParticipantsApi {

    public delegate: generated.MyTripParticipantsApi;

    constructor(fetch: generated.FetchAPI = portableFetch, basePath?: string) {
        super();
        this.delegate = new generated.MyTripParticipantsApi(this.configuration, basePath, fetch);
    }

    addTripParticipant(tripId: string, dummyBody: any, options?: any){
        return this.delegate.addTripParticipant(tripId, dummyBody, this.options(options));
    }

    deleteTripParticipant(tripId: string, participantId: number, options?: any){
        return this.delegate.deleteTripParticipant(tripId, participantId, this.options(options));
    }

    getTripParticipant(tripId: string, participantId: number, options?: any) {
        return this.delegate.getTripParticipant(tripId, participantId, this.options(options));
    }

    getTripParticipants(tripId: string, options?: any) {
        return this.delegate.getTripParticipants(tripId, this.options(options));
    }

    editTripParticipant(tripId: string, participantId: number, participant: generated.ParticipantDto, options?: any) {
        return this.delegate.editTripParticipant(tripId, participantId, participant, this.options(options));
    }
}
